/**
 * Who owns a row, and who is allowed to see everyone else's.
 *
 * Follow-up rows carry `bde_name`, which mirrors `bde_users.name` exactly
 * ("Rithika Yarramsetty", "Janvi y", " Siva Yarramsetty"). Nothing here is
 * hard-coded to a person — every BDA is matched by their own name, so a new
 * joiner works the day their account is created.
 *
 * The session user is NOT trusted for name/role: a Suite SSO login builds
 * `name` from the email when the token has no name, and defaults `role` to
 * 'bde'. Both would break scoping (a BDA matching nothing and seeing an empty
 * page, an admin wrongly restricted), so identity is resolved against the
 * `bde_users` roster by email first.
 */

/** Lowercase, trim, collapse inner whitespace. */
export const normName = (value) => (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Does `bdeName` refer to this person? Full name first, then first name — the
 * name on a lead is sometimes shortened ("Janvi y" vs "Janvi Yerramsetti").
 */
export const isOwnedBy = (bdeName, user) => {
  const mine = normName(user?.name || user?.username);
  const theirs = normName(bdeName);
  if (!mine || !theirs) return false;
  if (mine === theirs) return true;
  const myFirst = mine.split(' ')[0];
  const theirFirst = theirs.split(' ')[0];
  return !!myFirst && myFirst.length > 2 && myFirst === theirFirst;
};

/**
 * Resolve the signed-in user against the BDE roster.
 * `roster` is the array from dashboardAPI.getBDEUsers(); pass null while it loads.
 *
 * Returns { name, role, isAdmin, restrictToOwn, resolved }. Until the roster
 * arrives — or if it fails to load — `restrictToOwn` is false, so a hiccup
 * shows too much rather than an empty screen.
 */
export const resolveBdeIdentity = (user, roster) => {
  if (!user) return { name: '', role: '', isAdmin: false, restrictToOwn: false, resolved: false };
  const list = Array.isArray(roster) ? roster : null;
  const byEmail = list && normName(user.email)
    ? list.find(u => normName(u?.email) === normName(user.email))
    : null;
  const byId = !byEmail && list && user.id != null
    ? list.find(u => String(u?.id) === String(user.id))
    : null;
  const row = byEmail || byId || null;

  // A session name that is really an email address is useless for matching.
  const sessionName = (user.name || '').includes('@') ? '' : (user.name || user.username || '');
  const name = (row?.name || sessionName || '').trim();
  const email = normName(row?.email || user.email);
  const role = normName(row?.role || user.role);
  const isAdmin = role === 'admin';
  // Only scope down once we know who this is and that they are not an admin.
  const resolved = !!list && !!name;
  return { name, email, role, isAdmin, restrictToOwn: resolved && !isAdmin, resolved };
};

/**
 * The BDAs, for the "filter by person" dropdowns. Admins are accounts, not
 * people carrying leads, so they are left out; names are trimmed because the
 * roster has historically held stray whitespace that broke exact-match filters.
 */
export const bdaOptions = (roster) => (Array.isArray(roster) ? roster : [])
  .filter(u => u && (u.role || '').toLowerCase() !== 'admin')
  .map(u => ({ ...u, name: (u.name || '').trim() }))
  .filter(u => u.name)
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

/**
 * Does this record belong to me? Email first — it is exact and cannot be
 * misspelled — then the name, for older rows that only carry a name.
 */
export const isMine = (record, identity) => {
  if (!identity) return false;
  const owner = normName(record?.organizer_email);
  if (owner && identity.email && owner === identity.email) return true;
  return isOwnedBy(record?.bde_name || record?.organizer_name, identity);
};

/** Keep only the rows this identity may see. */
export const filterToOwnRows = (rows, identity, getName = (r) => r.bde_name) =>
  (identity?.restrictToOwn ? (rows || []).filter(r => isOwnedBy(getName(r), identity)) : (rows || []));
